import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Check, MapPin, DollarSign, Clock, Users, Briefcase, Heart, Award, ArrowRight, Quote, Building2, TrendingUp, Shield, Handshake } from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'

const ease = [0.16, 1, 0.3, 1] as const

const colors = {
  teal: '#145F6B',
  copper: '#9B775D',
  darkTeal: '#083B3A',
  cream: '#F6F6F1',
  nearBlack: '#0F0F0F',
}

const images = {
  stock5: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock5.png',
  stock9: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock9.png',
}

// ============================================
// HERO SECTION
// ============================================
const HeroSection: React.FC = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center pt-20 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={images.stock5} 
          alt="Luxury home" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
      </div>
      
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 py-32">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="text-[11px] tracking-[0.3em] uppercase mb-6"
            style={{ color: colors.copper }}
          >
            Franchise Opportunity
          </motion.p>
          
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="font-serif text-white mb-8"
            style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
          >
            Build a Business.<br />
            Shape a Community.<br />
            <span style={{ color: colors.copper }}>Transform Lives.</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
            className="text-xl lg:text-2xl text-white/80 leading-relaxed mb-10 max-w-2xl"
          >
            Becoming a Hudson My Home Franchise Owner offers the opportunity to build a premium 
            home management business, cultivate excellence in your market, and invest in a better 
            future for homeowners in your community.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.3 }}
            className="flex flex-wrap gap-4"
          >
            <a 
              href="#apply"
              className="inline-flex items-center gap-2 px-8 py-4 text-white text-[15px] font-medium transition-all hover:gap-3"
              style={{ backgroundColor: colors.teal }}
            >
              Join Our Candidate Community
              <ArrowRight className="w-4 h-4" />
            </a>
            <a 
              href="#requirements"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/30 text-white text-[15px] transition-all hover:bg-white/10"
            >
              View Requirements
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ============================================
// LIFE INVESTMENT SECTION
// ============================================
const LifeInvestmentSection: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease }}
          >
            <p 
              className="text-[11px] tracking-[0.3em] uppercase mb-4"
              style={{ color: colors.teal }}
            >
              More Than an Investment
            </p>
            <h2 
              className="font-serif text-grey-900 mb-6"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.15, letterSpacing: '-0.02em' }}
            >
              Being a Hudson Franchise Owner is a life investment
            </h2>
            <div className="space-y-5 text-lg text-grey-600 leading-relaxed">
              <p>
                Hudson Franchise Owners help continue the legacy of delivering white-glove home 
                management to discerning homeowners who value their time and their homes.
              </p>
              <p>
                While it is an exceedingly rewarding opportunity, it requires dedication, leadership, 
                and a genuine passion for service excellence. This is not a passive investment or a 
                side venture to add to your portfolio.
              </p>
              <p>
                It's a hands-on, life investment for leaders who want to build something meaningful—a 
                business that transforms how homeowners experience their homes and frees them to focus 
                on what matters most.
              </p>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
            className="relative"
          >
            <img 
              src={images.stock9} 
              alt="Premium home service" 
              className="w-full aspect-[4/3] object-cover"
            />
            <div 
              className="absolute -bottom-6 -left-6 p-6 lg:p-8 max-w-xs"
              style={{ backgroundColor: colors.cream }}
            >
              <p className="text-[13px] uppercase tracking-wider mb-2" style={{ color: colors.teal }}>
                Exclusive Territories
              </p>
              <p className="font-serif text-2xl text-grey-900">
                Limited markets available nationwide
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ============================================
// REQUIREMENTS SECTION
// ============================================
const RequirementsSection: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  
  const requirements = [
    {
      icon: MapPin,
      title: "Territory Rights",
      description: "Exclusive rights to operate a Hudson franchise in your designated market territory"
    },
    {
      icon: DollarSign,
      title: "Initial Investment",
      description: "$75,000 minimum in liquid, non-borrowed capital for the initial franchise fee"
    },
    {
      icon: Shield,
      title: "Financial Standing",
      description: "Strong credit history with no bankruptcies and minimum $250K net worth"
    },
    {
      icon: Clock,
      title: "Full Commitment",
      description: "Full-time, hands-on ownership and operation of your Hudson territory"
    },
    {
      icon: Briefcase,
      title: "Professional Experience",
      description: "7+ years of professional work experience, preferably in service or hospitality"
    },
    {
      icon: Users,
      title: "Leadership Track Record",
      description: "Demonstrated experience leading and developing high-performing teams"
    },
    {
      icon: Heart,
      title: "Service Excellence",
      description: "Genuine passion for delivering exceptional experiences and building relationships"
    },
    {
      icon: Building2,
      title: "Local Presence",
      description: "Established connections and residency in your target market community"
    },
  ]
  
  return (
    <section id="requirements" ref={ref} className="py-24 lg:py-32" style={{ backgroundColor: colors.cream }}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p 
            className="text-[11px] tracking-[0.3em] uppercase mb-4"
            style={{ color: colors.teal }}
          >
            Candidate Requirements
          </p>
          <h2 
            className="font-serif text-grey-900 mb-6"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.15, letterSpacing: '-0.02em' }}
          >
            Franchise Owner Requirements
          </h2>
          <p className="text-lg text-grey-600 max-w-2xl mx-auto">
            The Hudson franchise opportunity requires an entrepreneurial mindset, passion for service, 
            and unceasing determination. Meeting these minimum requirements does not guarantee selection.
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {requirements.map((req, index) => (
            <motion.div
              key={req.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease, delay: index * 0.1 }}
              className="bg-white p-8"
            >
              <div 
                className="w-14 h-14 flex items-center justify-center mb-6"
                style={{ backgroundColor: `${colors.teal}10` }}
              >
                <req.icon className="w-7 h-7" style={{ color: colors.teal }} />
              </div>
              <h3 className="font-serif text-xl text-grey-900 mb-3">{req.title}</h3>
              <p className="text-base text-grey-600 leading-relaxed">{req.description}</p>
            </motion.div>
          ))}
        </div>
        
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, ease, delay: 0.8 }}
          className="text-center text-sm text-grey-500 mt-12"
        >
          Our selection process is highly competitive. We carefully evaluate each candidate to ensure 
          the best fit for our brand and your success.
        </motion.p>
      </div>
    </section>
  )
}

// ============================================
// TESTIMONIALS SECTION
// ============================================
const TestimonialsSection: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  
  const testimonials = [
    {
      quote: "Becoming a Hudson Franchise Owner isn't about passive income. It's about investing your time and heart into building a culture of care that transforms how homeowners experience their lives.",
      name: "Michael R.",
      location: "Minneapolis, MN",
    },
    {
      quote: "Running a Hudson territory means being flexible and present. You're building relationships with your members while growing a team that shares your commitment to excellence.",
      name: "Sarah T.",
      location: "Phoenix, AZ",
    },
    {
      quote: "The first year is about building trust and reputation. The reward isn't just financial—it's seeing families reclaim their weekends and actually enjoy their homes.",
      name: "James L.",
      location: "Austin, TX",
    },
  ]
  
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p 
            className="text-[11px] tracking-[0.3em] uppercase mb-4"
            style={{ color: colors.teal }}
          >
            From Our Franchise Owners
          </p>
          <h2 
            className="font-serif text-grey-900"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.15, letterSpacing: '-0.02em' }}
          >
            Hear from those who've built it
          </h2>
        </motion.div>
        
        <div className="grid lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease, delay: index * 0.15 }}
              className="p-8 lg:p-10"
              style={{ backgroundColor: colors.cream }}
            >
              <Quote className="w-10 h-10 mb-6" style={{ color: colors.copper }} />
              <p className="text-lg text-grey-700 leading-relaxed mb-8 italic">
                "{testimonial.quote}"
              </p>
              <div className="border-t border-grey-200 pt-6">
                <p className="font-medium text-grey-900 text-lg">{testimonial.name}</p>
                <p className="text-sm text-grey-500">{testimonial.location}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ============================================
// BENEFITS SECTION
// ============================================
const BenefitsSection: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  
  const benefits = [
    {
      icon: Award,
      title: "Proven Business Model",
      description: "Leverage our established systems, technology platform, and operational playbooks."
    },
    {
      icon: TrendingUp,
      title: "Recurring Revenue",
      description: "Membership-based model creates predictable, growing monthly revenue."
    },
    {
      icon: Shield,
      title: "Exclusive Territory",
      description: "Protected market area with no competition from other Hudson franchises."
    },
    {
      icon: Handshake,
      title: "Ongoing Support",
      description: "Comprehensive training, marketing support, and operational guidance."
    },
  ]
  
  return (
    <section 
      ref={ref} 
      className="py-24 lg:py-32 text-white"
      style={{ backgroundColor: colors.darkTeal }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease }}
          >
            <p 
              className="text-[11px] tracking-[0.3em] uppercase mb-4"
              style={{ color: colors.copper }}
            >
              Why Hudson
            </p>
            <h2 
              className="font-serif mb-6"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.15, letterSpacing: '-0.02em' }}
            >
              We support you every step of the way
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-8">
              Hudson's success is directly tied to the passion and drive our Franchise Owners bring 
              to their local markets. That's why we invest heavily in your success—from comprehensive 
              training to ongoing operational support.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, ease, delay: 0.3 + index * 0.1 }}
                  className="flex gap-4"
                >
                  <div 
                    className="w-12 h-12 flex-shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                  >
                    <benefit.icon className="w-6 h-6" style={{ color: colors.copper }} />
                  </div>
                  <div>
                    <h3 className="font-medium text-white mb-1">{benefit.title}</h3>
                    <p className="text-sm text-white/60">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
            className="relative"
          >
            <div className="bg-white/5 border border-white/10 p-8 lg:p-12">
              <h3 className="font-serif text-2xl mb-8">What You Receive</h3>
              <ul className="space-y-4">
                {[
                  "Exclusive territory rights for your market",
                  "Complete operations manual and playbooks",
                  "Hudson mobile app and technology platform",
                  "Initial 4-week comprehensive training program",
                  "Marketing launch package and ongoing support",
                  "Vendor partnership network access",
                  "Ongoing coaching and business development",
                  "Annual franchise owner conference",
                  "Dedicated support team access",
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, ease, delay: 0.5 + index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: colors.copper }} />
                    <span className="text-white/80">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ============================================
// APPLICATION SECTION
// ============================================
const ApplicationSection: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  
  return (
    <section id="apply" ref={ref} className="py-24 lg:py-32" style={{ backgroundColor: colors.cream }}>
      <div className="max-w-[900px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-12"
        >
          <p 
            className="text-[11px] tracking-[0.3em] uppercase mb-4"
            style={{ color: colors.teal }}
          >
            Start Your Journey
          </p>
          <h2 
            className="font-serif text-grey-900 mb-6"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.15, letterSpacing: '-0.02em' }}
          >
            Join Our Candidate Community
          </h2>
          <p className="text-lg text-grey-600 max-w-2xl mx-auto">
            Our candidate community is our way of walking you through the selection process and 
            ensuring this is the right match for both of us. Complete the initial interest form 
            to begin the conversation.
          </p>
        </motion.div>
        
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="bg-white p-8 lg:p-12"
        >
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-grey-700 mb-2">First Name *</label>
              <input 
                type="text" 
                required
                className="w-full px-4 py-3 border border-grey-200 focus:border-grey-400 focus:outline-none transition-colors"
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-grey-700 mb-2">Last Name *</label>
              <input 
                type="text" 
                required
                className="w-full px-4 py-3 border border-grey-200 focus:border-grey-400 focus:outline-none transition-colors"
                placeholder="Smith"
              />
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-grey-700 mb-2">Email Address *</label>
              <input 
                type="email" 
                required
                className="w-full px-4 py-3 border border-grey-200 focus:border-grey-400 focus:outline-none transition-colors"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-grey-700 mb-2">Phone Number *</label>
              <input 
                type="tel" 
                required
                className="w-full px-4 py-3 border border-grey-200 focus:border-grey-400 focus:outline-none transition-colors"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-grey-700 mb-2">Target Market / City *</label>
              <input 
                type="text" 
                required
                className="w-full px-4 py-3 border border-grey-200 focus:border-grey-400 focus:outline-none transition-colors"
                placeholder="e.g., Denver, CO"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-grey-700 mb-2">Current Occupation *</label>
              <input 
                type="text" 
                required
                className="w-full px-4 py-3 border border-grey-200 focus:border-grey-400 focus:outline-none transition-colors"
                placeholder="e.g., Business Development Manager"
              />
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-grey-700 mb-2">Years of Professional Experience *</label>
            <select 
              required
              className="w-full px-4 py-3 border border-grey-200 focus:border-grey-400 focus:outline-none transition-colors bg-white"
            >
              <option value="">Select experience level</option>
              <option value="5-7">5-7 years</option>
              <option value="8-10">8-10 years</option>
              <option value="11-15">11-15 years</option>
              <option value="16-20">16-20 years</option>
              <option value="20+">20+ years</option>
            </select>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-grey-700 mb-2">Available Liquid Capital *</label>
            <select 
              required
              className="w-full px-4 py-3 border border-grey-200 focus:border-grey-400 focus:outline-none transition-colors bg-white"
            >
              <option value="">Select range</option>
              <option value="75k-100k">$75,000 - $100,000</option>
              <option value="100k-150k">$100,000 - $150,000</option>
              <option value="150k-250k">$150,000 - $250,000</option>
              <option value="250k+">$250,000+</option>
            </select>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-grey-700 mb-2">
              Why are you interested in becoming a Hudson Franchise Owner? *
            </label>
            <textarea 
              required
              rows={4}
              className="w-full px-4 py-3 border border-grey-200 focus:border-grey-400 focus:outline-none transition-colors resize-none"
              placeholder="Tell us about your background, motivations, and what excites you about this opportunity..."
            />
          </div>
          
          <div className="mb-8">
            <label className="flex items-start gap-3 cursor-pointer">
              <input 
                type="checkbox" 
                required
                className="mt-1 w-5 h-5 border-grey-300 rounded text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-grey-600">
                I confirm that I meet the minimum requirements and understand this is a full-time, 
                hands-on business opportunity. I agree to the privacy policy and terms of service.
              </span>
            </label>
          </div>
          
          <button 
            type="submit"
            className="w-full py-4 text-white text-[15px] font-medium transition-all hover:opacity-90"
            style={{ backgroundColor: colors.teal }}
          >
            Submit Application
          </button>
          
          <p className="text-center text-sm text-grey-500 mt-6">
            We review all applications within 5-7 business days. Qualified candidates will be 
            contacted for an initial phone consultation.
          </p>
        </motion.form>
      </div>
    </section>
  )
}

// ============================================
// MAIN PAGE
// ============================================
const FranchisePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroSection />
      <LifeInvestmentSection />
      <RequirementsSection />
      <TestimonialsSection />
      <BenefitsSection />
      <ApplicationSection />
      <Footer />
    </div>
  )
}

export default FranchisePage

